import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const heartbeatInterval = useRef(null);

  const isAuthenticated = !!token && !!user;

  // Heartbeat function to keep user online status updated
  const sendHeartbeat = useCallback(async () => {
    if (token) {
      try {
        await api.post("/auth/heartbeat");
      } catch (error) {
        // Silent fail - don't interrupt user
        console.debug("Heartbeat failed:", error);
      }
    }
  }, [token]);

  // Start heartbeat interval when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Send initial heartbeat
      sendHeartbeat();
      
      // Send heartbeat every 2 minutes
      heartbeatInterval.current = setInterval(sendHeartbeat, 2 * 60 * 1000);
      
      return () => {
        if (heartbeatInterval.current) {
          clearInterval(heartbeatInterval.current);
        }
      };
    }
  }, [isAuthenticated, sendHeartbeat]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Failed to load user:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    loadUser();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Check if user needs to select company
    if (userData.role !== "superadmin" && !userData.active_company_id && userData.company_ids?.length > 1) {
      navigate("/select-company");
    } else {
      navigate("/");
    }
    
    return userData;
  }, [navigate]);

  const register = useCallback(async (userData) => {
    const response = await api.post("/auth/register", userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  }, []);

  const selectCompany = useCallback(async (companyId) => {
    const response = await api.post("/auth/select-company", { company_id: companyId });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Don't navigate - let the caller decide
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    navigate("/login");
  }, [navigate]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    selectCompany,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
